import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import ConfirmPhoneNumberStep from '@/pages/getting-started/components/ConfirmPhoneNumberStep'

import { useAppSelector } from '@/redux/rootReducer'
import {
  selectAuthUser,
  updateMyProfile,
  selectAuthUserUpdateRequestState,
  startTwilioVerify,
  checkTwilioVerify,
  selectStartTwilioVerifyRequestTracker,
  selectCheckTwilioVerifyRequestTracker
} from '@/redux/reducers/auth.reducer'

export function ConfirmPhoneNumberStepContainer(props: { onComplete?: () => void }) {
  const { onComplete } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [phone, setPhone] = useState<string>('')
  const [status, setStatus] = useState<'initial' | 'pending'>('initial')

  const updateAuthUserRequestHandler = useRequestHandler({
    onError: () => enqueueSnackbar(t('notification.authUser.update.error'), { variant: 'error' }),
    onSuccess: () => {
      onComplete?.()
    },
    requestTrackerSelector: selectAuthUserUpdateRequestState
  })

  const startPhoneVerificationRequestHandler = useRequestHandler({
    onSuccess: () => {
      setStatus('pending')
    },
    onError: () => {
      enqueueSnackbar(t('notification.authUser.startPhoneVerification.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectStartTwilioVerifyRequestTracker
  })

  const checkPhoneVerificationRequestHandler = useRequestHandler({
    onSuccess: () => {
      updateAuthUserRequestHandler.dispatch(updateMyProfile({ isPhoneVerified: true, contactInfo: { phone } }))
    },
    onError: () => {
      enqueueSnackbar(t('notification.authUser.checkPhoneVerification.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectCheckTwilioVerifyRequestTracker
  })

  const authUser = useAppSelector(selectAuthUser)

  const initialValues = useMemo(() => ({
    phone: authUser?.contactInfo?.phone ?? '',
    code: ''
  }), [authUser])

  const handleSendPhoneNumber = (phone: string) => {
    setPhone(phone)
    startPhoneVerificationRequestHandler.dispatch(startTwilioVerify({ to: phone, channel: 'sms' }))
  }

  const handleVerifyPhoneNumber = (code: string) => {
    checkPhoneVerificationRequestHandler.dispatch(checkTwilioVerify({ to: phone, code }))
  }

  return (
    <ConfirmPhoneNumberStep
      initialValues={initialValues}
      isSubmitting={startPhoneVerificationRequestHandler.requestTracker.loading || checkPhoneVerificationRequestHandler.requestTracker.loading}
      onSendPhoneNumber={handleSendPhoneNumber}
      onVerifyPhoneNumber={handleVerifyPhoneNumber}
      status={status}
    />
  )
}

export default ConfirmPhoneNumberStepContainer